import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";

import ReactEcharts from "echarts-for-react";
import { format, addDays, addMonths, isSameMonth } from "date-fns";

import axios from "@app/lib/axios";

import CommonHorizontalBarChart from "@app/components/CommonHorizontalBarChart";
import CommonVerticalBarChart from "@app/components/CommonVerticalBarChart";

import CardStatContent, { CardStatBranchContent } from "../Components/CardStatContent";

import style from "../Components/style";

const Branch = forwardRef(({ statPeriod, selectedViewOption, updateViewOption }, ref) => {
    const [statArranged, setStatArranged] = useState(false);
    const [total, setTotal] = useState(false);
    const [average, setAverage] = useState(false);
    const [viewOptionApplied, setViewOptionApplied] = useState(false);

    const typeTitle = selectedViewOption === "playCount" ? "순" : "누적";
    const statIndex = selectedViewOption === "playCount" ? 0 : 1;

    const initStat = () => {
        setStatArranged(false);
    };

    useImperativeHandle(ref, () => ({
        getExcelData(callback) {
            const excelData = {
                fileName: `토모노트_지사콘텐츠(${typeTitle})통계`,
                sheets: [
                    {
                        name: "콘텐츠",
                        title: `토모노트 지사콘텐츠(${typeTitle}) 통계`,
                        columnTitle: ["지사", `${typeTitle} 실행 수`],
                        data: statArranged ? statArranged[statIndex].map((s) => [s.branchName, s[selectedViewOption] || 0]) : [],
                    },
                ],
            };
            callback(null, excelData);
        },
    }));
    const getStat = () => {
        initStat();
        axios({
            method: "POST",
            url: `/api/stat_note/hq/branches`,
            data: {
                startDate: statPeriod.startDate,
                endDate: statPeriod.endDate,
            },
        })
            .then((result) => {
                if (result.data.code === 200) {
                    const stat = result.data;
                    applyStat(stat.branches);
                } else {
                    alert(result.data && result.data.msg ? result.data.msg : "통계 정보를 가져오는데 실패하였습니다.\n지속 발생시 관리자에게 문의해주세요.");
                }
            })
            .catch((error) => {
                console.log(error);
                console.error("알 수 없는 오류가 발생하였습니다. \n지속 발생시 관리자에게 문의해주세요.");
            });
    };

    // 상/중/하 구분 %
    const averageBase = [
        { name: "상위", percent: 20, display: 20 },
        { name: "중위", percent: 60, display: 60 },
        { name: "하위", percent: 100, display: 20 },
    ];
    const applyStat = (stats) => {
        const statArranged = [[], []];
        const total = [0, 0];
        const average = [[], []];
        if (stats && stats.length > 0) {
            // 총합
            stats.forEach((s) => {
                total[0] += s.playCount || 0;
                total[1] += s.playCountSum || 0;
            });
            const averageBaseCount = averageBase.map((a) => Math.floor((stats.length / 100) * a.percent));
            let averageBaseIndex = 0;
            let sumForAvg = 0;
            // 정렬 - 누적(playCount desc)
            // 누적 순으로 정렬되어 내려옴
            //stats.sort((a, b) => b.playCountSum - a.playCountSum);
            stats.forEach((s, index) => {
                statArranged[1].push({ ...s });
                if (index >= averageBaseCount[averageBaseIndex]) {
                    average[1].push(sumForAvg);
                    averageBaseIndex++;
                    sumForAvg = 0;
                }
                sumForAvg += s.playCountSum || 0;
            });
            average[1].push(sumForAvg);

            // 정렬 - 순(playCount desc)
            averageBaseIndex = 0;
            sumForAvg = 0;
            stats.sort((a, b) => b.playCount - a.playCount);
            stats.forEach((s, index) => {
                statArranged[0].push({ ...s });
                if (index >= averageBaseCount[averageBaseIndex]) {
                    average[0].push(sumForAvg);
                    averageBaseIndex++;
                    sumForAvg = 0;
                }
                sumForAvg += s.playCount || 0;
            });
            average[0].push(sumForAvg);
        }
        setStatArranged(statArranged);
        setTotal(total);
        setAverage(average);
    };
    useEffect(() => {
        if (!viewOptionApplied && updateViewOption) {
            updateViewOption(
                [
                    { name: "콘텐츠 순 실행 수", id: "playCount" },
                    { name: "콘텐츠 누적 실행 수", id: "playCountSum" },
                ],
                "playCount"
            );
            setViewOptionApplied(true);
        }
        if (viewOptionApplied) {
            getStat();
        }
        return () => {};
    }, [statPeriod, selectedViewOption, viewOptionApplied]);

    const chartOption = {
        xAxis: {
            field: "branchName",
            option: {
                axisLabel: {
                    show: true,
                    interval: 0,
                    rotate: 45,
                },
            },
        },
        yAxis: {
            name: "건",
            series: [style.seriesBranchPlayCount[statIndex]],
        },
        stacks: [{ stack: "base", title: `${typeTitle} 실행 수`, showSum: false, showSumTooltip: true, showPercent: true }],
        showLegend: true,
        legend: {
            x: "right",
            y: "top",
        },
        sort: false,
        grid: {
            top: "10%",
            left: "5%",
            right: "5%",
            bottom: "15%",
        },
    };

    return (
        <div className="stat-branch-content">
            <div className="row mb-3">
                <div className="col-xl-3 col-lg-3 col-md-4 col-sm-6 col-12">
                    <CardStatContent id="1" title={`콘텐츠 ${typeTitle} 실행 수`} titleBgClass="bg-tomonote">
                        <span className="">전체: {total ? total[statIndex].toLocaleString() : "-"}</span>
                    </CardStatContent>
                </div>
                <div className="col-xl-6 col-lg-6 col-md-4 col-sm-6 col-12">
                    <CardStatContent id="2" title={`평균 ${typeTitle} 실행 수`} titleBgClass="bg-tomonote">
                        <span className="">
                            {averageBase.map((a, index) => {
                                return (
                                    <span key={`key-card-span-${index}`} className="ml-3">{`${a.name} ${a.display}% : ${
                                        average && average[statIndex] && average[statIndex][index] ? average[statIndex][index].toLocaleString() : "-"
                                    }`}</span>
                                );
                            })}
                        </span>
                    </CardStatContent>
                </div>
                <div className="col-xl-3 col-lg-3 col-md-4 col-sm-6 col-12">
                    <CardStatBranchContent id="3" top={statArranged ? statArranged[statIndex][0] : false} last={statArranged ? statArranged[statIndex][statArranged[statIndex].length - 1] : false} />
                </div>
            </div>
            <div className="row mb-3">
                <div className="col">
                    <div className="card">
                        <div className="card-body" style={{ height: "500px", textAlign: "center" }}>
                            <CommonHorizontalBarChart chartData={statArranged ? statArranged[statIndex] : false} chartOption={chartOption} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default Branch;
